import {Component, OnDestroy, OnInit} from '@angular/core';
import {LibraryDetails} from '../../../models/library.model';
import {LibraryService} from '../../../services/library.service';
import {ActivatedRoute, Router} from '@angular/router';
import {DialogService} from '../../../services/dialog.service';
import {MatDialog} from '@angular/material/dialog';
import {PolicyAssignerComponent} from '../policy-assigner/policy-assigner.component';
import {BehaviorSubject, Observable, Subject, of} from 'rxjs';
import {catchError, finalize, map, tap} from 'rxjs/operators';
import {JobService} from '../../../services/job.service';
import {TranslateService} from '@ngx-translate/core';
import {CustomSnackBarService} from '../../../services/custom-snackbar.service';
import {UserService} from '../../../services/user.service';
import {extractErrorMessage} from '../../../utils/error-utils';

@Component({
  selector: 'app-libraries-detail',
  templateUrl: './libraries-detail.component.html',
  styleUrls: ['./libraries-detail.component.scss']
})
export class LibrariesDetailComponent implements OnInit, OnDestroy {
  public details: LibraryDetails;
  public recalculateSubject = new Subject<boolean>();
  public openAssignerSubject = new Subject<boolean>();
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();
  private globalLoadingSubject = new BehaviorSubject<boolean>(false);
  public globalLoading$ = this.globalLoadingSubject.asObservable();
  private unbindingSubject = new BehaviorSubject<boolean>(false);
  public unbinding$ = this.unbindingSubject.asObservable();
  public reloadAssignedRetentionsTableSubject = new Subject<boolean>();

  constructor(private libraryService: LibraryService,
              private activatedRoute: ActivatedRoute,
              private router: Router,
              private userService: UserService,
              private dialogService: DialogService,
              private jobService: JobService,
              private dialog: MatDialog,
              private customSnackBarService: CustomSnackBarService,
              private translate: TranslateService) {
  }

  ngOnInit(): void {
    const libraryId = this.activatedRoute.snapshot.params.id;
    this.get(libraryId).subscribe();
    this.initSubjects();
  }

  ngOnDestroy(): void {
    this.unsubscribe();
  }

  get(libraryId: string): Observable<void> {
    if (!libraryId) {
      const message = this.translate.instant('dialog.noLibrary');
      const dialogTitle = this.translate.instant('dialog.warning');
      this.dialogService.error(message, dialogTitle);
      this.router.navigate(['libraries/active']);
      return of(undefined);
    }
    this.loadingSubject.next(true);
    return this.libraryService.details(libraryId).pipe(
      tap(resp => {
        this.loadingSubject.next(false);
        this.globalLoadingSubject.next(false);
        this.setData(resp);
      }),
      catchError(error => {
        this.loadingSubject.next(false);
        this.globalLoadingSubject.next(false);
        this.unbindingSubject.next(false);
        this.dialogService.error(extractErrorMessage(error));
        return of(undefined);
      }),
      map(() => undefined)
    );
  }

  setData(details: LibraryDetails): void {
    this.unbindingSubject.next(false);
    this.details = details;
    if (this.details.retentionBindings) {
      this.details.retentionBindings.forEach(binding => {
        const parts: string [] = binding.retentionId.split('@');
        binding.retentionDisplayName = parts[0] + ' - ' + parts[1];
      });
    }
  }

  recalculate(): void {
    this.loadingSubject.next(true);
    this.libraryService.recalculate(this.details.libraryId).pipe(
      finalize(() => this.loadingSubject.next(false))
    ).subscribe({
      next: (resp) => {
        resp.retentionBindings = this.details.retentionBindings;
        resp.hasRootFolderRetention = this.details.hasRootFolderRetention;
        this.setData(resp);
      },
      error: (error) => {
        this.dialogService.error(extractErrorMessage(error));
      }
    });
  }

  openPolicyAssigner(): void {
    this.dialog.open(PolicyAssignerComponent, {
      data: {libraryDetails: this.details},
      width: '600px'
    }).afterClosed().subscribe((choice) => {
      if (choice) {
        this.get(this.details.libraryId).subscribe();
      }
    });
  }

  confirmUnbindAll(): void {
    const unbindMessage = this.translate.instant('dialog.unbindAll', {library: this.details.displayName});
    const dialogTitle = this.translate.instant('dialog.confirmation');
    this.dialogService.plain(unbindMessage, dialogTitle)
      .afterClosed().subscribe((choice) => {
      if (choice) {
        this.unbindAll();
      }
    });
  }


  unbindAll(): void {
    this.unbindingSubject.next(true);
    this.libraryService.unbindAll(this.details.libraryId).subscribe({
      next: (resp) => {
        this.get(this.details.libraryId).subscribe();
        this.unbindingSubject.next(true);
        if (resp?.failedUnboundPolicyIds && Object.keys(resp.failedUnboundPolicyIds).length > 0) {
          const errorMessages = Object.values(resp.failedUnboundPolicyIds).map(String).join('\n');
          this.dialogService.error(errorMessages);
        }
      },
      error: (error) => {
        this.unbindingSubject.next(false);
        this.dialogService.error(extractErrorMessage(error));
      }
    });
  }

  recalculateRetentionProperties(): void {
    this.globalLoadingSubject.next(true);
    this.jobService.recalculateRetentions(this.details.libraryId).subscribe({
      next: () => {
        this.customSnackBarService.openSuccessSnackbar(this.translate.instant('libraries.edit.folders.recalculateSnackbarMessage'));
        this.get(this.details.libraryId).pipe(
          finalize(() => this.globalLoadingSubject.next(false))
        ).subscribe();
      },
      error: (error) => {
        this.globalLoadingSubject.next(false);
        this.dialogService.error(extractErrorMessage(error));
      }
    });
  }

  back(): void {
    const sub = this.isAllCriteriaMet() ? 'active' : 'pending';
    const to = 'libraries/' + sub;
    this.router.navigate([to]);
  }

  get status(): 'not-calculated' | 'no-policy-at-root' | 'ready' | 'error' | null {
    if (!this.details) {
      return null;
    }
    if (this.isNotYetCalculated()) {
      return 'not-calculated';
    }
    if (this.isWithoutPolicyAtRoot()) {
      return 'no-policy-at-root';
    }
    if (this.isReady() || this.isAllCriteriaMet()) {
      return 'ready';
    }
    return 'error';
  }

  isNotYetCalculated(): boolean {
    return !!this.details && this.details.activationThreshold == null;
  }

  isAllCriteriaMet(): boolean {
    return !this.isNotYetCalculated() && !!this.details && (!this.details.activationThreshold || this.details.activationThreshold.length === 0);
  }

  isReady(): boolean {
    return !this.isNotYetCalculated() && !!this.details && !this.details.retentionBindings
      && (this.onlyRootFolderNotAssigned() || !this.details.activationThreshold || this.details.activationThreshold.length === 0);
  }

  isWithErrors(): boolean {
    return !this.isNotYetCalculated() && !!this.details && (!this.isReady() && !!this.details.retentionBindings);
  }

  isError(): boolean {
    return this.status === 'error';
  }

  isWithoutPolicyAtRoot(): boolean {
    return !!this.details && !this.details.hasRootFolderRetention && !!this.details.retentionBindings && this.details.retentionBindings.length > 0;
  }

  onlyRootFolderNotAssigned(): boolean {
    return !!this.details.activationThreshold && this.details.activationThreshold.length === 1 && this.details.activationThreshold[0] === 'The root folder has no policy assigned.';
  }

  isSuperAdmin(): boolean {
    return this.userService.isSuperAdmin();
  }

  gotoLibrarySettingsUrl(): void {
    const url = this.libraryService.getLibrarySettingsUrl(this.details.libraryId);
    window.open(url, '_blank');
  }

  initSubjects(): void {
    this.initRecalculateSubject();
    this.initOpenAssignerSubject();
    this.initReloadAssignedRetentionsTableSubject();
  }

  unsubscribe(): void {
    this.recalculateSubject.unsubscribe();
    this.openAssignerSubject.unsubscribe();
    this.reloadAssignedRetentionsTableSubject.unsubscribe();
  }

  initRecalculateSubject(): void {
    this.recalculateSubject.subscribe(() => this.recalculate());
  }

  initOpenAssignerSubject(): void {
    this.openAssignerSubject.subscribe(() => this.openPolicyAssigner());
  }

  initReloadAssignedRetentionsTableSubject(): void {
    this.reloadAssignedRetentionsTableSubject.subscribe(() => this.get(this.details.libraryId).subscribe());
  }

}
